import { motion } from 'motion/react';
import { Check, Award, Star, Share2 } from 'lucide-react';
import { useState } from 'react';

interface ProductRecommendationScreenProps {
  persona: string;
  onRestart: () => void;
  onMembershipRecomendation: () => void;
  onMembershipSignup: () => void;
  sharer?: () => void;
}

export function ProductRecommendationScreen({ persona, onRestart, onMembershipRecomendation, onMembershipSignup, sharer }: ProductRecommendationScreenProps) {
  // Persona-specific data

  const personaData: Record<string, { tagline: string; quote: string; color: string, link: string }> = {
    'Livsnyderen': {
      tagline: 'Som Livsnyderen hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker frihed til at udforske nye muligheder',
      color: '#D76F2C',
      link: ''  
    },
    'Redebyggeren': {
      tagline: 'Som Redebyggeren hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker tryghed for mig og dem omkring mig',
      color: '#008E9F',
      link: ''
    },
    'Den selvkørende': {
      tagline: 'Som Den selvkørende hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker at skabe fremdrift på min egen måde',
      color: '#D76F2C',
      link: ''
    },
    'Den forberedte': {
      tagline: 'Som Den forberedte hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker tryghed gennem grundig planlægning',
      color: '#008E9F', 
      link: ''
    },
    'Boss i egen biks': {
      tagline: 'Som Boss i egen biks hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker frihed til at bygge min egen vej',
      color: '#D76F2C', 
      link: ''
    },
    'Førstejobberen': {
      tagline: 'Som Førstejobberen hjælper vi dig med at stå stærkt i hverdagen.',
      quote: 'Jeg ønsker støtte i starten af min rejse',
      color: '#008E9F',
      link: ''
    }
  };

  const currentPersona = personaData[persona] || personaData['Livsnyderen'];
  
  // Get persona image
  const getPersonaImage = () => {
    switch (persona) {
      case 'Den selvkørende':
        return '/assets/images/personalityTest/2c33ee0d4fc6ed90f6f41420c293e36f68052e13.png';
      case 'Førstejobberen':
        return '/assets/images/personalityTest/04e47bb404eff7ad41f294d79adc7e2780f2a2dd.png';
      case 'Den forberedte':
        return '/assets/images/personalityTest/9e3354e5824f40b2bdecf9d1b0e22375fb623282.png';
      case 'Boss i egen biks':
        return '/assets/images/personalityTest/0fca243103754d4932d85ae3957b2cee045142e8.png';
      case 'Livsnyderen':
        return '/assets/images/personalityTest/fff6657fb8013fcfdd03c1a4da72344eea2876e1.png';
      case 'Redebyggeren':
        return '/assets/images/personalityTest/beb963681be4d10fe200743b9d82a71f11c75470.png';
      default:
        return null;
    }
  };

  const personaImage = getPersonaImage();

  

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-bg-soft pb-12"
    >
      {/* Header Section */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white pt-8 pb-12 px-6"
        style={{ boxShadow: 'var(--shadow-soft)' }}
      >
        <div className="max-w-4xl mx-auto text-center space-y-6">
          {/* Persona Illustration */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            className="w-32 h-32 mx-auto rounded-full flex items-center justify-center overflow-hidden"
            style={{ 
              background: `linear-gradient(135deg, ${currentPersona.color}15, ${currentPersona.color}05)`,
              boxShadow: 'var(--shadow-soft)'
            }}
          >
            {personaImage ? (
              <img src={personaImage} alt={persona} className="w-full h-full object-cover" />
            ) : (
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="23" cy="27" r="3" fill={currentPersona.color} />
                <circle cx="41" cy="27" r="3" fill={currentPersona.color} />
                <path d="M20 36 Q32 42 44 36" stroke={currentPersona.color} strokeWidth="3" strokeLinecap="round" fill="none" />
                <circle cx="32" cy="32" r="20" stroke={currentPersona.color} strokeWidth="2" fill="none" />
                <path d="M14 16 Q12 10 14 8" stroke={currentPersona.color} strokeWidth="2" strokeLinecap="round" fill="none" />
                <path d="M50 16 Q52 10 50 8" stroke={currentPersona.color} strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            )}
          </motion.div>

          {/* Persona Name */}
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h1 className="text-gray-900">Du er {persona}</h1>
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-600 max-w-md mx-auto"
          >
            {currentPersona.tagline}
          </motion.p>
        </div>
      </motion.div>

      {/* Section Title */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="max-w-4xl mx-auto px-6 pt-12 pb-6 text-center"
      >
        <h2 className="text-gray-900">Din personlige anbefaling</h2>
        <p className="text-gray-600 mt-2">Vi har udvalgt det, der passer bedst til dig.</p>
        <p>{persona}</p>
      </motion.div>

      {/* CTA Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="max-w-2xl mx-auto px-6 pt-8"
        >
          <button
            className="btn button w-full bg-ase-orange text-white py-4 px-6 transition-all active:scale-95 personalitytest"
            style={{ boxShadow: 'var(--shadow-soft)' }}
            onClick={onMembershipRecomendation}
          >
            Videre til din anbefalingen
          </button>

          <button
            className="btn button w-full bg-ase-petroleum text-white py-4 px-6 transition-all active:scale-95 personalitytest"
            style={{ boxShadow: 'var(--shadow-soft)' }}
            onClick={onMembershipSignup}
          >
            Bliv medlem
          </button>

          <button className="w-full flex items-center justify-center gap-2 text-ase-petroleum py-3 transition-all active:scale-95 personalitytest"
             onClick={() => {
              if (sharer) {
                sharer();
              }
             }
            }>
            <Share2 className="w-5 h-5" strokeWidth={1.5} />
            Del testen med venner
          </button>
        </motion.div>

      {/* Trust Signals */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.0 }}
        className="max-w-4xl mx-auto px-6 pt-8"
      >
        <div className="flex flex-wrap justify-center gap-6 text-gray-600">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-ase-petroleum" strokeWidth={2} />
            <span>Myndighedsgodkendt</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-ase-orange fill-ase-orange" strokeWidth={2} />
            <span>Høj medlemstilfredshed</span>
          </div>
        </div>
      </motion.div>

      {/* Persona Quote Banner */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="max-w-2xl mx-auto px-6 pt-8"
      >
        <div 
          className="rounded-3xl p-6 text-center"
          style={{ 
            backgroundColor: `${currentPersona.color}08`,
            border: `1px solid ${currentPersona.color}20`
          }}
        >
          <p className="text-gray-700 italic">"{currentPersona.quote}"</p>
        </div>
      </motion.div>

      {/* Take Test Again Link */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="max-w-4xl mx-auto px-6 pt-8 pb-8 text-center"
      >
        <button
          onClick={onRestart}
          className="text-ase-petroleum hover:text-ase-orange transition-colors underline personalitytest"
        >
          Tag testen igen
        </button>
      </motion.div>
    </motion.div>
  );
}